import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCardFrontImageSrc } from '../lib/cardImages';
import type { Orientation, SpreadType, TarotCard } from '../lib/types';

type SessionBeat = {
  eyebrow: string;
  text: string;
};

type SessionCard = {
  card: TarotCard;
  orientation: Orientation;
  positionLabel: string;
  stageLabel: string;
  firstGlance: string;
  mood: string;
  beats: SessionBeat[];
  bridge: string;
};

const BEAT_REVEAL_MS = 900;
const SESSION_SPREAD: SpreadType = 'three-card';

const SESSION_CARDS: SessionCard[] = [
  {
    card: {
      id: 'major-09',
      name_en: 'The Hermit',
      name_kr: '은둔자',
      arcana: 'major',
      number: 9,
      keywords_upright: ['내면 탐색', '고요한 분별', '혼자만의 시간'],
      keywords_reversed: ['고립', '과도한 거리두기', '답을 미루기'],
      meaning_short: '속도를 늦추고 안쪽의 진짜 목소리를 듣는 카드',
    },
    orientation: 'upright',
    positionLabel: '첫 번째 장면',
    stageLabel: '시작점',
    firstGlance: '첫 장부터 소리가 작아져. 이건 일이 터진 뒤의 카드가 아니라, 오래 참고 안으로 접어 넣은 흔적이 먼저 나온 판이야.',
    mood: '묘안이 느끼는 기류: 조용한 긴장, 말 못 한 피로, 이미 마음속에서는 결론이 자라난 상태',
    beats: [
      {
        eyebrow: '묘안의 첫인상',
        text: '은둔자가 시작점에 놓였다는 건, 문제의 출발이 행동 그 자체보다 "혼자 판단하려던 버릇"에서 시작됐다는 뜻이야. 겉으로는 차분했어도 속에서는 계속 계산하고 있었던 거지.',
      },
      {
        eyebrow: '타로이스트가 짚는 장면',
        text: '이 카드는 보통 사람들 사이에서 부딪혀서 꼬인 흐름보다, 혼자 정리하다가 타이밍을 놓친 흐름에서 더 선명해져. 누군가와 상의할 문제도 결국 네 안에서만 몇 바퀴를 돈 느낌이 강해.',
      },
      {
        eyebrow: '왜 지금 이 카드가 나왔는지',
        text: '리딩의 첫 장면에서 은둔자가 나오는 건 "이미 오래 생각했다"는 증거야. 아직 말로 꺼내진 않았어도, 네 안에서는 이 문제를 그냥 넘길 수 없다고 판단한 지 꽤 됐다는 뜻이지.',
      },
    ],
    bridge: '좋아, 시작점은 잡혔어. 이제 두 번째 장에서, 네가 왜 아직도 이 흐름을 붙들고 있는지 더 아픈 지점을 볼게.',
  },
  {
    card: {
      id: 'swords-02',
      name_en: 'Two of Swords',
      name_kr: '소드 2',
      arcana: 'minor',
      suit: 'swords',
      number: 2,
      keywords_upright: ['유보', '판단 보류', '균형'],
      keywords_reversed: ['결정 회피', '감정 차단', '긴장 누적'],
      meaning_short: '답을 알고도 결정을 미루는 순간의 긴장',
    },
    orientation: 'upright',
    positionLabel: '두 번째 장면',
    stageLabel: '지금 걸리는 핵심',
    firstGlance: '가운데 카드가 꽤 솔직해. 답이 없어서 멈춘 게 아니라, 답의 책임이 무거워서 멈춘 결이야.',
    mood: '묘안이 느끼는 기류: 버티는 침묵, 확신을 미루는 손끝, 선택 이후의 파장을 두려워하는 마음',
    beats: [
      {
        eyebrow: '앞 카드와의 연결',
        text: '방금 은둔자가 "혼자 품고 오래 생각한 상태"를 보여줬다면, 소드 2는 그 생각 끝에서 아직 칼을 내리지 못하고 있는 장면이야. 이미 답을 모르는 건 아니야. 다만 그 답을 실행하는 순간 생길 변화가 무거운 거지.',
      },
      {
        eyebrow: '타로이스트가 보는 본심',
        text: '이 카드는 감정이 없는 사람이 아니라, 감정을 잠깐 얼려 놓은 사람에게 자주 나와. 스스로 흔들릴까 봐, 일단 판단을 보류하고 표정을 고정해 둔 느낌이 아주 강해.',
      },
      {
        eyebrow: '지금 필요한 설명',
        text: '몰입감 있는 리딩에서는 여기서 결론을 주면 안 돼. 이 장의 역할은 "무엇이 막고 있나"를 드러내는 거야. 그래서 이 카드는 답보다 긴장을, 사건보다 태도를 먼저 보여줘.',
      },
    ],
    bridge: '이제 마지막 장을 보자. 이 판이 결국 어디로 기울 수 있는지, 그리고 네가 어떤 기준을 세워야 하는지 여기서 정리될 거야.',
  },
  {
    card: {
      id: 'major-17',
      name_en: 'The Star',
      name_kr: '별',
      arcana: 'major',
      number: 17,
      keywords_upright: ['회복', '신뢰', '투명한 희망'],
      keywords_reversed: ['낙담', '기대의 흔들림', '회복 지연'],
      meaning_short: '정직한 회복과 다시 숨을 고르는 희망',
    },
    orientation: 'upright',
    positionLabel: '세 번째 장면',
    stageLabel: '흐름의 도착지',
    firstGlance: '마지막 장이 별이면 무턱대고 괜찮아진다는 뜻은 아니야. 대신, 기준을 다시 세우면 흐름을 회복할 여지가 분명히 남아 있다는 뜻이야.',
    mood: '묘안이 느끼는 기류: 숨이 트이는 정직함, 감정보다 맑아진 시야, 다시 믿어볼 수 있는 방향감',
    beats: [
      {
        eyebrow: '결말의 온도',
        text: '은둔자에서 시작된 혼자만의 판단, 소드 2에서 굳어버린 유보가 별에서 풀리는 건 "마음을 예쁘게 달래라"가 아니라, 기준을 투명하게 정리하면 판이 맑아진다는 얘기야.',
      },
      {
        eyebrow: '타로이스트의 종합',
        text: '이 세 장은 감정의 드라마보다 의사결정의 질을 보여줘. 네가 흔들린 이유는 약해서가 아니라, 너무 오래 혼자 계산했기 때문이야. 별은 거기서 숨구멍을 내주는 카드야.',
      },
      {
        eyebrow: '사용자에게 남길 한 줄',
        text: '마지막 장면에서 중요한 건 희망을 믿으라는 말이 아니야. 네가 진짜로 감당할 수 있는 기준을 세우면, 이미 흐름은 다시 움직일 준비가 되어 있다는 점이야.',
      },
    ],
    bridge: '세 장을 다 봤어. 이제 이 세션이 왜 더 몰입감 있게 느껴지는지, 어떤 정보 구조로 LLM을 짜야 하는지도 아래에서 정리해 둘게.',
  },
];

function buildCardStackTransform(index: number, currentIndex: number): string {
  const distance = index - currentIndex;

  if (distance === 0) return 'translate3d(0, 0, 0) scale(1) rotate(0deg)';
  if (distance < 0) return `translate3d(${distance * 72}px, ${Math.abs(distance) * 24}px, 0) scale(0.88) rotate(${-8 + distance * 2}deg)`;

  return `translate3d(${distance * 60}px, ${distance * 22}px, 0) scale(0.9) rotate(${6 + distance * 2}deg)`;
}

function buildMobilePeekTransform(index: number, currentIndex: number): string {
  const distance = index - currentIndex;

  if (distance === 0) return 'translate3d(0, 0, 0) scale(1) rotate(0deg)';
  if (distance < 0) return `translate3d(${distance * 24}px, ${Math.abs(distance) * 16}px, 0) scale(0.9) rotate(-10deg)`;

  return `translate3d(${distance * 22}px, ${distance * 16}px, 0) scale(0.9) rotate(10deg)`;
}

export default function ImmersiveReadingLabPage() {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [visibleBeatCount, setVisibleBeatCount] = useState(1);

  const currentCard = SESSION_CARDS[currentCardIndex];
  const isLastCard = currentCardIndex === SESSION_CARDS.length - 1;

  useEffect(() => {
    setVisibleBeatCount(1);

    const timer = window.setInterval(() => {
      setVisibleBeatCount((count) => {
        if (count >= currentCard.beats.length) {
          window.clearInterval(timer);
          return count;
        }

        return count + 1;
      });
    }, BEAT_REVEAL_MS);

    return () => window.clearInterval(timer);
  }, [currentCardIndex, currentCard.beats.length]);

  const visibleBeats = currentCard.beats.slice(0, visibleBeatCount);

  const sessionNotes = useMemo(
    () => [
      '카드를 펼치자마자 결론을 말하지 않고, 먼저 공기와 표정을 읽는다.',
      '두 번째 카드부터는 반드시 앞 카드와 연결해 이야기한다.',
      '마지막 조언은 세 개가 아니라 한 개만 남긴다.',
      '카드 뜻 설명보다 "왜 지금 이 카드가 여기 나왔는지"를 먼저 말한다.',
    ],
    []
  );

  return (
    <main className="min-h-screen overflow-hidden bg-[#09070b] text-[#f6efe3]">
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(190,139,80,0.22),_transparent_38%),radial-gradient(circle_at_80%_20%,_rgba(88,58,120,0.28),_transparent_26%),linear-gradient(180deg,_#120d11_0%,_#09070b_55%,_#09070b_100%)]" />
        <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:72px_72px]" />
      </div>

      <section className="mx-auto flex min-h-screen max-w-[1500px] flex-col gap-8 px-5 pb-16 pt-20 lg:px-10">
        <div className="flex flex-col gap-4 border-b border-white/10 pb-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="mb-3 text-[11px] uppercase tracking-[0.4em] text-[#c89b61]">Myoan Session Lab</p>
            <h1 className="font-['Song_Myung'] text-4xl leading-[1.05] text-[#fff6ea] sm:text-5xl lg:text-6xl">
              카드 한 장씩
              <br />
              타로이스트가 눈앞에서 풀어주는 리딩
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-[#cfc4b4] sm:text-base">
              현재 페이지를 건드리지 않고 추가한 임시 프로토타입입니다. 핵심은 카드 뜻을 정리하는 화면이 아니라,
              묘안이 한 장씩 넘겨 보며 공기부터 읽고, 앞 카드와 연결해 설명하는 세션처럼 느껴지게 만드는 데 있습니다.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-[#d9ccb8]">
            <Link
              to="/spreads"
              className="rounded-full border border-white/15 px-4 py-2 transition hover:border-[#d5a462] hover:text-white"
            >
              스프레드 선택으로 이동
            </Link>
            <Link
              to="/"
              className="rounded-full bg-[#f4eee3] px-4 py-2 font-medium text-[#120d11] transition hover:bg-white"
            >
              메인으로 돌아가기
            </Link>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.25fr_0.75fr]">
          <section className="rounded-[32px] border border-white/10 bg-white/[0.03] p-5 shadow-[0_40px_120px_rgba(0,0,0,0.42)] backdrop-blur md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.38em] text-[#c89b61]">Prototype Session</p>
                  <h2 className="mt-2 text-2xl font-semibold text-[#fff7eb]">가까운 미래, 내 선택의 흐름은?</h2>
                </div>

                <div className="rounded-full border border-[#c89b61]/30 bg-[#1a1216] px-4 py-2 text-xs text-[#e9d8be]">
                  {currentCard.positionLabel} · {currentCard.stageLabel}
                </div>
              </div>

              <div className="grid gap-8 xl:grid-cols-[0.8fr_1.2fr]">
                <div className="relative flex min-h-[460px] items-center justify-center overflow-hidden rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_top,_rgba(200,155,97,0.18),_transparent_35%),linear-gradient(180deg,_rgba(255,255,255,0.03),_rgba(255,255,255,0.01))] px-8 py-10">
                  <div className="absolute left-6 top-6 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] uppercase tracking-[0.32em] text-[#d9bc8d]">
                    {SESSION_SPREAD === 'three-card' ? 'three card spread' : 'reading'}
                  </div>

                  <div className="relative flex h-full w-full items-center justify-center md:hidden">
                    <div className="relative h-[360px] w-[240px]">
                      {SESSION_CARDS.map((sessionCard, index) => {
                        const transform = buildMobilePeekTransform(index, currentCardIndex);
                        const isActive = index === currentCardIndex;

                        return (
                          <div
                            key={`mobile-${sessionCard.card.id}`}
                            className="absolute left-1/2 top-1/2 h-[320px] w-[210px] -translate-x-1/2 -translate-y-1/2 transition duration-700 ease-out"
                            style={{
                              transform: `${transform} translate(-50%, -50%)`,
                              opacity: Math.abs(index - currentCardIndex) > 1 ? 0 : 1,
                              zIndex: 20 - Math.abs(index - currentCardIndex),
                            }}
                          >
                            <div className={`relative h-full overflow-hidden rounded-[24px] border ${isActive ? 'border-[#d5a462]/60 shadow-[0_30px_80px_rgba(213,164,98,0.22)]' : 'border-white/10'} bg-[#120d11]`}>
                              <img
                                src={getCardFrontImageSrc(sessionCard.card)}
                                alt={sessionCard.card.name_kr}
                                className={`h-full w-full object-cover ${sessionCard.orientation === 'reversed' ? 'rotate-180' : ''}`}
                              />
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent px-4 pb-5 pt-12">
                                <p className="text-[10px] uppercase tracking-[0.36em] text-[#d6b17a]">{sessionCard.positionLabel}</p>
                                <p className="mt-2 text-lg font-semibold text-white">{sessionCard.card.name_kr}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="relative hidden h-[360px] w-[240px] md:block md:h-[420px] md:w-[280px]">
                    {SESSION_CARDS.map((sessionCard, index) => {
                      const transform = buildCardStackTransform(index, currentCardIndex);
                      const isActive = index === currentCardIndex;

                      return (
                        <div
                          key={sessionCard.card.id}
                          className="absolute left-1/2 top-1/2 h-[320px] w-[210px] -translate-x-1/2 -translate-y-1/2 transition duration-700 ease-out sm:h-[380px] sm:w-[250px]"
                          style={{
                            transform: `${transform} translate(-50%, -50%)`,
                            opacity: index < currentCardIndex - 1 ? 0 : 1,
                            zIndex: 20 - Math.abs(index - currentCardIndex),
                          }}
                        >
                          <div className={`relative h-full overflow-hidden rounded-[24px] border ${isActive ? 'border-[#d5a462]/60 shadow-[0_30px_80px_rgba(213,164,98,0.18)]' : 'border-white/10'} bg-[#120d11]`}>
                            <img
                              src={getCardFrontImageSrc(sessionCard.card)}
                              alt={sessionCard.card.name_kr}
                              className={`h-full w-full object-cover ${sessionCard.orientation === 'reversed' ? 'rotate-180' : ''}`}
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent px-4 pb-5 pt-12">
                              <p className="text-[10px] uppercase tracking-[0.36em] text-[#d6b17a]">{sessionCard.positionLabel}</p>
                              <p className="mt-2 text-lg font-semibold text-white">{sessionCard.card.name_kr}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col justify-between rounded-[28px] border border-white/10 bg-[#120d11]/70 p-6">
                  <div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.34em] text-[#d1a260]">묘안의 첫 시선</p>
                        <h3 className="mt-2 text-2xl font-semibold text-[#fff6e8]">{currentCard.firstGlance}</h3>
                      </div>
                      <span className="hidden rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-[#aa9780] sm:inline-flex">
                        beat {visibleBeatCount}/{currentCard.beats.length}
                      </span>
                    </div>

                    <p className="mt-4 rounded-2xl border border-[#c89b61]/20 bg-[#1a1418] px-4 py-3 text-sm leading-6 text-[#dccbb5]">
                      {currentCard.mood}
                    </p>

                    <div className="mt-6 space-y-4">
                      {visibleBeats.map((beat, index) => (
                        <article
                          key={`${currentCard.card.id}-${index}`}
                          className="rounded-[22px] border border-white/8 bg-white/[0.03] px-4 py-4 transition duration-500"
                        >
                          <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">{beat.eyebrow}</p>
                          <p className="mt-3 text-[15px] leading-8 text-[#f1e7d8]">{beat.text}</p>
                        </article>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 border-t border-white/8 pt-5">
                    <p className="text-sm leading-7 text-[#d9cbb7]">{currentCard.bridge}</p>

                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setCurrentCardIndex((index) => Math.max(index - 1, 0))}
                        disabled={currentCardIndex === 0}
                        className="rounded-full border border-white/12 px-4 py-2 text-sm text-[#f4ede2] transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-35"
                      >
                        이전 카드
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentCardIndex((index) => Math.min(index + 1, SESSION_CARDS.length - 1))}
                        disabled={isLastCard}
                        className="rounded-full bg-[#f4eee3] px-5 py-2 text-sm font-medium text-[#120d11] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        다음 카드 보기
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentCardIndex(0)}
                        className="rounded-full border border-[#c89b61]/35 px-4 py-2 text-sm text-[#dcbf92] transition hover:border-[#dcbf92] hover:text-white"
                      >
                        세션 다시 보기
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
                <section className="rounded-[24px] border border-white/10 bg-[#0f0b0e] p-5">
                  <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Session Frame</p>
                  <ul className="mt-4 space-y-3 text-sm leading-7 text-[#d4c5b1]">
                    {SESSION_CARDS.map((sessionCard, index) => (
                      <li
                        key={sessionCard.card.id}
                        className={`rounded-2xl border px-4 py-3 transition ${index === currentCardIndex ? 'border-[#d5a462]/45 bg-[#1b1416] text-white' : 'border-white/8 bg-white/[0.02]'}`}
                      >
                        <span className="block text-[10px] uppercase tracking-[0.32em] text-[#be9766]">{sessionCard.positionLabel}</span>
                        <span className="mt-2 block text-base font-medium">{sessionCard.card.name_kr}</span>
                        <span className="mt-1 block text-sm text-[#c8b9a4]">{sessionCard.stageLabel}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="rounded-[24px] border border-white/10 bg-[#140f13] p-5">
                  <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Why This Feels Better</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {sessionNotes.map((note) => (
                      <article key={note} className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4 text-sm leading-7 text-[#e7dccd]">
                        {note}
                      </article>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <section className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.05),_rgba(255,255,255,0.02))] p-6">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">LLM Direction</p>
              <h2 className="mt-3 text-2xl font-semibold text-[#fff7ea]">보고서가 아니라 세션처럼</h2>
              <div className="mt-5 space-y-4 text-sm leading-7 text-[#d6c8b2]">
                <p>이 프로토타입은 카드마다 같은 문단 길이로 설명하지 않습니다. 대신 첫인상, 해석, 앞 카드 연결, 다음 카드로 넘기는 숨을 분리합니다.</p>
                <p>사용자는 카드 뜻 백과사전을 읽는 것이 아니라, 타로이스트가 시선을 옮길 때마다 무엇을 먼저 보는지 따라가게 됩니다.</p>
              </div>
            </section>

            <section className="rounded-[28px] border border-white/10 bg-[#110d10] p-6">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Research Input</p>
              <div className="mt-4 space-y-4">
                <article className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-semibold text-[#fff5e8]">Tarot Talks Live</p>
                  <p className="mt-2 text-sm leading-7 text-[#d4c4ae]">사전 녹화보다 라이브 리딩처럼 보이는 화면이 몰입을 만듭니다. 카메라가 카드를 넘기듯 단계가 진행되어야 합니다.</p>
                </article>
                <article className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-semibold text-[#fff5e8]">Guided Journey Flow</p>
                  <p className="mt-2 text-sm leading-7 text-[#d4c4ae]">질문 입력부터 결과까지 한 번에 보여주기보다, 각 단계마다 사용자의 감정을 예열시키는 흐름이 중요합니다.</p>
                </article>
                <article className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-semibold text-[#fff5e8]">Premium Interpretation Deliverable</p>
                  <p className="mt-2 text-sm leading-7 text-[#d4c4ae]">마지막에는 문장 수를 늘리기보다, 들은 내용을 다시 붙잡을 수 있는 짧고 강한 종합 정리가 남아야 합니다.</p>
                </article>
              </div>
            </section>

            <section className="rounded-[28px] border border-[#c89b61]/20 bg-[#170f13] p-6">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Temporary Route</p>
              <p className="mt-3 text-lg font-medium text-[#fff6eb]">/lab/immersive-reading</p>
              <p className="mt-3 text-sm leading-7 text-[#d6c7b2]">
                기존 페이지는 그대로 두고 실험만 분리한 경로입니다. 프롬프트 구조, 카드별 정보 설계, 애니메이션 리듬을 검토할 때 이 화면을 기준점으로 쓰면 됩니다.
              </p>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
}
